import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  FaroButton,
  FaroText,
  LayoutSheetSmallIcon,
  NO_TRANSLATE_CLASS,
  PointCloudIcon,
  TranslateVar,
  dataComparisonColorsSorted,
  grey,
  useToast,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import {
  IElementSectionDataSession,
  isIElementSectionGeoslam,
} from "@faro-lotv/ielement-types";
import { selectChildrenDepthFirst } from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import Stack from "@mui/material/Stack";
import { useCallback, useMemo, useState, type JSX } from "react";
import { UpdatedLocalPose } from "../utils/multi-registration-report";

/** Info displayed in the alignment bar of the inspect & publish tool */
type InspectAndPublishProps = {
  /** Name of the current sheet */
  areaName?: string;
  /** The data sessions the user is viewing. */
  dataSessions: IElementSectionDataSession[];

  // TODO: Actual redirect will be implemented with https://faro01.atlassian.net/browse/NRT-1007
  /**
   * Optional, enables redirect to the dashboard after the workflow finishes if not undefined.
   * If not undefined, the string is used to call redirectToDashboardProjectPage which allows
   * to define the target tab of the project details in the dashboard.
   * If undefined the Viewer will be opened after the registration is saved or the merge is started.
   */
  dashboardRedirect?: string;

  /** The local poses which have been calculated during registration. */
  updatedLocalIElementPoses?: UpdatedLocalPose[] | null;
};

/**
 * @returns Utility bar that provides buttons for registration refinement and confirming the result
 */
export function InspectAndPublishBar({
  areaName,
  dataSessions,
}: InspectAndPublishProps): JSX.Element {
  const store = useAppStore();
  const { preAlignmentApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();
  const { openToast } = useToast();

  const [isStartingPreAlignmentTask, setIsStartingPreAlignmentTask] =
    useState(false);

  const hasVisualRegistrationFeature = useAppSelector(
    selectHasFeature(Features.VisualRegistration),
  );

  const hasPreAlignmentFeature = useAppSelector(
    selectHasFeature(Features.PreAlignment),
  );

  const orbisDataSessions = useMemo(
    () =>
      dataSessions.filter(
        (session) =>
          selectChildrenDepthFirst(
            session,
            isIElementSectionGeoslam,
            1,
          )(store.getState()).length !== 0,
      ),
    [store, dataSessions],
  );

  /** Callback to trigger the orbis pre-alignment */
  const startPreAlignment = useCallback(async () => {
    assert(
      preAlignmentApiClient,
      "Pre-Alignment API client is not defined, cannot start pre-alignment",
    );

    if (orbisDataSessions.length < 2) {
      handleErrorWithToast({
        title: "Failed to start automatic pre-alignment",
        error:
          "At least two Orbis data sessions are required for automatic pre-alignment",
      });
      return;
    }
    setIsStartingPreAlignmentTask(true);

    try {
      await preAlignmentApiClient.startPreAlignment({
        sheetIds: orbisDataSessions.map((session) => session.id),
      });
      openToast({
        title: "Automatic pre-alignment started.",
        message: "Task started successfully.",
        variant: "success",
      });
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to start pre-alignment task",
        error,
      });
    }

    setIsStartingPreAlignmentTask(false);
  }, [
    preAlignmentApiClient,
    orbisDataSessions,
    handleErrorWithToast,
    openToast,
  ]);

  return (
    <Stack direction="column">
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1,
          px: 4,
          backgroundColor: grey[100],
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "left",
            gap: 1,
            overflow: "hidden",
          }}
        >
          {areaName ? (
            <>
              <LayoutSheetSmallIcon />
              {/* Will change this once we switch from sheet to area in all user-facing strings */}
              <FaroText variant="bodyS">
                Sheet: <TranslateVar name="areaName">{areaName}</TranslateVar>
              </FaroText>
            </>
          ) : null}
          {/* With visual registration, the point clouds are shown in the side bar instead */}
          {!hasVisualRegistrationFeature && (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "left",
                gap: 2,
                overflowX: "auto",
              }}
            >
              {dataSessions.map((dataSession, index) => (
                <Stack
                  key={dataSession.id}
                  direction="row"
                  sx={{ alignItems: "center", gap: 1 }}
                >
                  <PointCloudIcon
                    sx={{
                      color:
                        dataComparisonColorsSorted[
                          index % dataComparisonColorsSorted.length
                        ],
                    }}
                  />
                  <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
                    {dataSession.name}
                  </FaroText>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "left",
            gap: 2,
            overflow: "hidden",
          }}
        >
          {hasPreAlignmentFeature && preAlignmentApiClient && (
            <FaroButton
              variant="secondary"
              title="Run automatic pre-alignment"
              onClick={startPreAlignment}
              isLoading={isStartingPreAlignmentTask}
            >
              Run automatic pre-alignment
            </FaroButton>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
